import React, { useEffect, useState, useCallback } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import * as authentikOidc from '../auth/authentikOidc';
import './PaymentSuccess.css';

/**
 * PaymentSuccess Page, post-Stripe-checkout entry point (Flow A).
 *
 * After a successful Stripe payment the customer lands here logged-out.
 * The webhook has already created (or will shortly create) their Authentik
 * account from the Stripe email; we just need them to enroll a password.
 *
 * Flow:
 *   1. Verify the Stripe session with the backend (purely informational -
 *      the webhook is the source of truth).
 *   2. Show celebratory UI with trial info.
 *   3. CTA kicks the user into Authentik's enrollment flow with their email
 *      pre-filled. Authentik handles password setup + redirects back via
 *      the OAuth code-flow to /auth/callback.
 */
const PaymentSuccess = () => {
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('session_id');
  const emailParam = searchParams.get('email') || '';

  // State machine: verifying -> success -> error
  const [status, setStatus] = useState(emailParam ? 'success' : 'verifying');
  const [email, setEmail] = useState(emailParam);
  const [errorMessage, setErrorMessage] = useState('');
  const [trialEnd, setTrialEnd] = useState(null);

  const API_URL = process.env.REACT_APP_API_URL || '';

  // Verify payment with backend (best-effort, surfaces a friendly error
  // if Stripe returns an unpaid/expired session).
  const verifyPayment = useCallback(async () => {
    if (!sessionId) {
      // No session_id but we have an email param, still allow enrollment.
      if (emailParam) {
        setStatus('success');
        return;
      }
      setStatus('error');
      setErrorMessage('Geen sessie-ID gevonden. Neem contact op met support.');
      return;
    }

    try {
      const response = await fetch(`${API_URL}/api/checkout/success/${sessionId}`);
      const data = await response.json();

      if (data.success) {
        if (data.email) setEmail(data.email);
        if (data.trial_end) setTrialEnd(data.trial_end);
        setStatus('success');
      } else {
        setStatus('error');
        setErrorMessage(data.message || 'Betaling nog niet voltooid');
      }
    } catch (error) {
      console.error('Payment verification error:', error);
      setStatus('error');
      setErrorMessage('Kon betalingsstatus niet verifiëren');
    }
  }, [sessionId, emailParam, API_URL]);

  useEffect(() => {
    verifyPayment();
  }, [verifyPayment]);

  // Kick into Authentik enrollment with the Stripe email pre-filled.
  const handleStartEnrollment = () => {
    authentikOidc.login({
      email,
      flow: 'enrollment',
      returnTo: '/app',
    });
  };

  // Format trial end date
  const formatTrialEnd = (dateString) => {
    if (!dateString) return '7 dagen';
    const date = new Date(dateString);
    return date.toLocaleDateString('nl-NL', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  // ─────────────────────────────────────────────────────────────────────────────
  // RENDER: Loading / Verifying
  // ─────────────────────────────────────────────────────────────────────────────
  if (status === 'verifying') {
    return (
      <div className="ps-page">
        <div className="ps-card">
          <div className="ps-icon-wrapper loading">
            <span className="material-icons ps-spinner">sync</span>
          </div>
          <h1 className="ps-title">Betalingsstatus controleren...</h1>
        </div>
      </div>
    );
  }

  // ─────────────────────────────────────────────────────────────────────────────
  // RENDER: Error
  // ─────────────────────────────────────────────────────────────────────────────
  if (status === 'error') {
    return (
      <div className="ps-page">
        <div className="ps-card">
          <div className="ps-icon-wrapper error">
            <span className="material-icons">error_outline</span>
          </div>
          <h1 className="ps-title">Er is iets misgegaan</h1>
          <p className="ps-error">{errorMessage}</p>
          <div className="ps-actions">
            <Link to="/" className="ps-button">
              <span className="material-icons">arrow_back</span>
              Terug naar home
            </Link>
          </div>
        </div>
      </div>
    );
  }

  // ─────────────────────────────────────────────────────────────────────────────
  // RENDER: Success, celebratory + enrollment CTA
  // ─────────────────────────────────────────────────────────────────────────────
  return (
    <div className="ps-page">
      <div className="ps-card">
        <div className="ps-icon-wrapper success">
          <span className="material-icons">celebration</span>
        </div>

        <h1 className="ps-title">Betaling geslaagd!</h1>

        <p className="ps-subtitle">
          Je 7-daagse gratis proefperiode is gestart. Nog één korte stap:
          maak je account aan zodat je kunt inloggen.
        </p>

        {/* Trial info tile */}
        <div className="ps-info-tile">
          <div className="ps-info-icon">
            <span className="material-icons">schedule</span>
          </div>
          <div className="ps-info-content">
            <h2>Gratis Trial</h2>
            <p>
              Je trial eindigt op <strong>{formatTrialEnd(trialEnd)}</strong>.
              Na de proefperiode wordt automatisch &euro;49/maand afgeschreven.
            </p>
            <p style={{ fontSize: '0.85rem', color: '#64748b', marginTop: '8px' }}>
              De &euro;0,01 op je afschrift is een kaartverificatie en wordt
              automatisch teruggestort.
            </p>
          </div>
        </div>

        {/* Email confirmation */}
        {email && (
          <div className="ps-steps" style={{ marginBottom: '24px' }}>
            <h3>Account aanmaken voor</h3>
            <p
              style={{
                padding: '12px 16px',
                background: '#fff',
                border: '1px solid #e2e8f0',
                borderRadius: '10px',
                margin: 0,
                color: '#0f172a',
                fontSize: '1rem',
                textAlign: 'center',
              }}
            >
              {email}
            </p>
          </div>
        )}

        {/* Next steps */}
        <div className="ps-steps">
          <h3>Volgende stappen</h3>
          <div className="ps-steps-list">
            <div className="ps-step">
              <div className="ps-step-number">1</div>
              <div className="ps-step-content">
                <strong>Maak je account aan</strong>
                <span>Kies een wachtwoord op de volgende pagina</span>
              </div>
            </div>
            <div className="ps-step">
              <div className="ps-step-number">2</div>
              <div className="ps-step-content">
                <strong>Log in</strong>
                <span>Je wordt automatisch doorgestuurd naar de app</span>
              </div>
            </div>
            <div className="ps-step">
              <div className="ps-step-number">3</div>
              <div className="ps-step-content">
                <strong>Begin met vacatures schrijven</strong>
                <span>Volledige toegang tot alle functies</span>
              </div>
            </div>
          </div>
        </div>

        {/* Action button */}
        <div className="ps-actions" style={{ marginTop: '28px' }}>
          <button
            type="button"
            onClick={handleStartEnrollment}
            className="ps-button"
          >
            <span className="material-icons">person_add</span>
            Maak je account aan
          </button>
        </div>

        {/* Support */}
        <div className="ps-support" style={{ marginTop: '28px' }}>
          <p>
            Hulp nodig? Neem contact op met{' '}
            <a href="mailto:support@humanaize.nl">support@humanaize.nl</a>
          </p>
        </div>
      </div>
    </div>
  );
};

export default PaymentSuccess;
